import React from 'react';
import { AdminModalTw, btnDanger, btnPrimary, btnSecondary } from './adminUi';

export type AdminNav =
  | 'overview'
  | 'industries'
  | 'sectors'
  | 'companies'
  | 'records'
  | 'record-grading'
  | 'grading'
  | 'community'
  | 'permissions'
  | 'subscriptions';

export const STRUCTURE_ADMIN_NAV: AdminNav[] = ['industries', 'sectors', 'grading', 'permissions', 'subscriptions'];

export type AdminNavPermissions = {
  canManageStructure: boolean;
  canEditRecords: boolean;
  canGradeRecords: boolean;
};

export function defaultAdminNav(perms: AdminNavPermissions): AdminNav {
  if (perms.canManageStructure) return 'overview';
  if (perms.canEditRecords) return 'records';
  if (perms.canGradeRecords) return 'record-grading';
  return 'records';
}

export function isAdminNavAllowed(nav: AdminNav, perms: AdminNavPermissions): boolean {
  if (nav === 'permissions' || nav === 'subscriptions') return perms.canManageStructure;
  if (perms.canManageStructure) return true;
  if (nav === 'records') return perms.canEditRecords;
  if (nav === 'record-grading') return perms.canGradeRecords;
  if (STRUCTURE_ADMIN_NAV.includes(nav)) return false;
  return perms.canEditRecords || perms.canGradeRecords;
}

export type AdminLocation =
  | { screen: 'overview' }
  | { screen: 'industries' }
  | { screen: 'sectors'; industryId?: number }
  | { screen: 'companies'; industryId?: number; sectorId?: number }
  | {
      screen: 'company';
      companyId: number;
      industryId?: number;
      sectorId?: number;
      quickAdd?: boolean;
      openRecord?: boolean;
    }
  | { screen: 'records' }
  | { screen: 'record-grading' }
  | { screen: 'grading' }
  | { screen: 'community' }
  | { screen: 'permissions' }
  | { screen: 'subscriptions' };

export type BreadcrumbItem = {
  label: string;
  onClick?: () => void;
};

export {
  AdminBreadcrumbsTw as AdminBreadcrumbs,
  AdminModalTw as AdminModal,
  AdminModalTw,
  AdminFieldTw as AdminField,
  AdminFieldTw,
} from './adminUi';

export function ConfirmDialog({
  title,
  message,
  confirmLabel = 'Подтвердить',
  onConfirm,
  onCancel,
  danger,
  confirmDisabled,
}: {
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  danger?: boolean;
  confirmDisabled?: boolean;
}) {
  return (
    <AdminModalTw
      title={title}
      onClose={onCancel}
      footer={
        <>
          <button type="button" className={btnSecondary} onClick={onCancel} disabled={confirmDisabled}>
            Отмена
          </button>
          <button type="button" className={danger ? btnDanger : btnPrimary} onClick={onConfirm} disabled={confirmDisabled}>
            {confirmLabel}
          </button>
        </>
      }
    >
      <div className="text-sm leading-relaxed text-slate-600">{message}</div>
    </AdminModalTw>
  );
}

export function parseNumberInput(value: string): number | null {
  const normalized = value.replace(/\s/g, '').replace(',', '.');
  if (!normalized.trim()) return null;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

/** Оставляет только цифры и одну десятичную точку (без % и прочих символов). */
export function sanitizePercentInput(value: string): string {
  let normalized = value.replace(/[^\d.,]/g, '').replace(',', '.');
  const dotIndex = normalized.indexOf('.');
  if (dotIndex === -1) return normalized;
  const intPart = normalized.slice(0, dotIndex);
  const fracPart = normalized.slice(dotIndex + 1).replace(/\./g, '');
  return `${intPart}.${fracPart}`;
}

/** STI из БД (доля) → строка для поля в процентах. */
export function stiDecimalToPercentInput(decimal: number | null | undefined): string {
  if (decimal == null || Number.isNaN(decimal)) return '';
  const percent = Math.round(decimal * 1000) / 10;
  return String(percent);
}

/** Проценты из поля → доля для API/БД. */
export function parseStiPercentToDecimal(value: string): number {
  const percent = parseNumberInput(value);
  if (percent == null) return 0;
  return percent / 100;
}

export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}
